import { addItems } from './items'
import { expeditionCostFor, rollLoot } from './loot'
import { expeditionParticipantIds, LOCATIONS } from './locations'
import { getVehicleDef, getVehicleEffectiveStatsForInstance } from './vehicles'
import type {
  Expedition,
  ItemStack,
  Survivor,
  TripParticipantOutcome,
  TripReport,
  VehicleInstance,
} from './types'

const clamp = (n: number) => Math.max(0, Math.min(100, n))
const INJURY_CHANCE = 0.15
const randomIntBetween = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min

export type TickInput = {
  survivors: Survivor[]
  expeditions: Expedition[]
  garageVehicles: VehicleInstance[]
  now: number
}

export type TickOutput = {
  survivors: Survivor[]
  expeditions: Expedition[]
  reports: Record<string, TripReport>
  resolvedCount: number
}

export function resolveExpeditions(input: TickInput): TickOutput {
  const { now } = input
  const due = input.expeditions.filter(
    (e) => e.status === 'active' && e.endsAt <= now,
  )
  if (due.length === 0) {
    return {
      survivors: input.survivors,
      expeditions: input.expeditions,
      reports: {},
      resolvedCount: 0,
    }
  }

  const survivors: Survivor[] = input.survivors.map((s) => ({
    ...s,
    needs: { ...s.needs },
  }))
  const reports: Record<string, TripReport> = {}
  const returned: Record<string, Expedition> = {}
  const vehicleById: Record<string, VehicleInstance> = Object.fromEntries(
    input.garageVehicles.map((v) => [v.id, v]),
  )

  for (const exp of due) {
    const ids = expeditionParticipantIds(exp)
    const vehicleInstanceIds =
      exp.vehicleInstanceIds ??
      (exp.vehicleInstanceId ? [exp.vehicleInstanceId] : [])
    const vehicleMult =
      vehicleInstanceIds.length > 0
        ? vehicleInstanceIds.reduce((sum, id) => {
            const inst = vehicleById[id]
            if (!inst) return sum
            const def = getVehicleDef(inst.defId)
            if (!def) return sum
            return sum + getVehicleEffectiveStatsForInstance(inst).lootMultiplier
          }, 0) / Math.max(1, vehicleInstanceIds.length)
        : (() => {
            const vehicleDefIds = exp.vehicleDefIds ?? (exp.vehicleDefId ? [exp.vehicleDefId] : [])
            return vehicleDefIds.length > 0
              ? vehicleDefIds.reduce((sum, id) => sum + (getVehicleDef(id)?.lootMultiplier ?? 1), 0) /
                  vehicleDefIds.length
              : 1
          })()

    let mergedLoot: ItemStack[] = []
    for (const sid of ids) {
      const idx = survivors.findIndex((s) => s.id === sid)
      if (idx < 0) continue
      const survivor = survivors[idx]
      mergedLoot = addItems(
        mergedLoot,
        rollLoot(exp.locationId, survivor, vehicleMult),
      )
    }

    const participantOutcomes: TripParticipantOutcome[] = []
    for (const sid of ids) {
      const idx = survivors.findIndex((s) => s.id === sid)
      if (idx < 0) continue
      const survivor = survivors[idx]
      const cost = expeditionCostFor(exp.locationId, survivor)
      const injured = Math.random() < INJURY_CHANCE
      const injuryLevel = LOCATIONS[exp.locationId].injuryLevel
      const injuryDurationMs = injured
        ? randomIntBetween(injuryLevel.minDurationMs, injuryLevel.maxDurationMs)
        : 0
      const needsBefore = {
        hunger: survivor.needs.hunger,
        thirst: survivor.needs.thirst,
        energy: survivor.needs.energy,
      }
      const needsAfter = {
        hunger: clamp(survivor.needs.hunger - cost.hunger),
        thirst: clamp(survivor.needs.thirst - cost.thirst),
        energy: clamp(survivor.needs.energy - cost.energy - (injured ? 10 : 0)),
      }
      survivors[idx] = {
        ...survivor,
        status: injured ? 'Injured' : 'Idle',
        injuryEndsAt: injured ? now + injuryDurationMs : null,
        needs: needsAfter,
      }
      participantOutcomes.push({
        survivorId: sid,
        needsBefore,
        needsAfter,
        injured,
      })
    }

    if (participantOutcomes.length === 0) continue

    const leaderId = exp.survivorId
    const leaderRow =
      participantOutcomes.find((o) => o.survivorId === leaderId) ?? participantOutcomes[0]
    const anyInjured = participantOutcomes.some((o) => o.injured)

    reports[exp.id] = {
      locationId: exp.locationId,
      survivorId: leaderRow.survivorId,
      collectedItems: mergedLoot,
      needsBefore: leaderRow.needsBefore,
      needsAfter: leaderRow.needsAfter,
      injured: anyInjured,
      participantOutcomes,
    }
    returned[exp.id] = {
      ...exp,
      status: 'returned',
      returnedAt: now,
      report: reports[exp.id],
    }
  }

  const expeditions = input.expeditions.map((exp) => returned[exp.id] ?? exp)

  return {
    survivors,
    expeditions,
    reports,
    resolvedCount: due.length,
  }
}
